import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { MESSAGE_SNIPPET_KEYS } from '../../constants/messageSnippets';
import type { CopilotState, StepRefs } from '../../data-layer/types';
import { setActiveStep, setFormStatus } from '../../data-store/copilotSlice';
import { useChatFlowController } from '../../hooks/useChatFlowController';
import ErrorBanner from '../ErrorBanner/ErrorBanner';
import FilePromptComponent from '../FilePromptComponent/FilePromptComponent';
import Responses from '../Responses/Responses';
import { StepComponentFactory } from '../StepComponents';

import './ChatFlow.css';
import '../StepComponents/StepComponents.css';

// Local interface for Redux state
interface AppState {
  copilot: CopilotState;
}

interface ChatFlowProps {
  containerRef: React.RefObject<HTMLDivElement>;
  stepRefs: StepRefs;
  scrollToStep: (stepName: string) => void;
}

/**
 * ChatFlow component matching Copilot1 design
 */
function ChatFlow({ containerRef, stepRefs, scrollToStep }: ChatFlowProps) {
  const dispatch = useDispatch();
  const { messages } = useSelector((state: AppState) => state.copilot);
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const { handleFormSubmit, handleSkip, controller } = useChatFlowController();

  // Auto-scroll to most recently added message by timestamp
  useEffect(() => {
    if (messages.length > 0) {
      // Find the most recently created message by timestamp
      const mostRecentMessage = messages.reduce((latest, current) =>
        current.timestamp > latest.timestamp ? current : latest
      );

      setTimeout(() => {
        const messageElement = document.querySelector(
          `[data-message-id="${mostRecentMessage.id}"]`
        );

        if (messageElement) {
          messageElement.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
          });
        } else {
          // Fallback to scroll to bottom
          const chatContainer = document.querySelector('.Copilot-ChatFlow');

          if (chatContainer) {
            chatContainer.scrollTop = chatContainer.scrollHeight;
          }
        }
      }, 100);
    }
  }, [messages]);

  const handleButtonClick = (
    action: string,
    context?: { label?: string; stepName?: string }
  ) => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
    (controller as any)?.handleButtonClick?.(action, context);
  };

  const handleStepClick = (stepName: string) => {
    if (stepName === MESSAGE_SNIPPET_KEYS.finalConfirmationRedirection) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
      (controller as any)?.handleNavigationBackToListing?.(
        MESSAGE_SNIPPET_KEYS.finalConfirmationRedirection
      );

      return;
    }

    // Set the clicked step as active so new messages appear below it
    dispatch(setActiveStep(stepName));
    dispatch(setFormStatus({ stepName, formStatus: 'expandedview' }));
    scrollToStep(stepName);
  };

  return (
    <div className='Copilot-ChatFlow' ref={containerRef}>
      {/* Render messages from Redux */}
      {messages.map((message) => {
        if (message.type === 'ai_response') {
          // Handle error messages - show ErrorBanner in chat flow
          if (message.subType === 'error') {
            return (
              <div
                key={message.id}
                data-message-id={message.id}
                className='Copilot-Prompt-Loader Copilot-Prompt-Loader--top'>
                <ErrorBanner message={message.text || 'An error occurred'} />
              </div>
            );
          }

          const responseButtons = message.buttons?.map((button) => ({
            label: button.label,
            iconKind: button.iconKind,
            onClick: () =>
              handleButtonClick(button.action, {
                label: button.label,
                stepName: message.stepName,
              }),
          }));

          return (
            <div
              key={message.id}
              data-message-id={message.id}
              className='Copilot-Prompt-Loader Copilot-Prompt-Loader--top Copilot-ChatFlow-segregateWelcomeMsg'>
              <Responses
                type={message.subType}
                loader={message.loader}
                buttons={responseButtons}
                values={message.values}
                text={message.text}
                onStepClick={handleStepClick}
              />
            </div>
          );
        }

        if (message.type === 'user_response') {
          return (
            <div
              key={message.id}
              data-message-id={message.id}
              className='Copilot-UserInput'>
              <div className='Copilot-UserInput-Container'>
                <div className='Copilot-UserInput-Text'>{message.text}</div>
              </div>
            </div>
          );
        }

        if (message.type === 'step_form') {
          const stepRef = stepRefs[message.stepName as keyof typeof stepRefs];

          return (
            <div
              key={message.id}
              data-message-id={message.id}
              className='Copilot-StepForm Copilot-ChatFlow-BetweenSteps'
              ref={stepRef}>
              <StepComponentFactory
                message={message}
                onFormSubmit={handleFormSubmit}
                onSkip={handleSkip}
              />
            </div>
          );
        }

        if (message.type === 'file_prompt') {
          return (
            <div
              key={message.id}
              data-message-id={message.id}
              className='Copilot-UserInput'>
              <div className='Copilot-UserInput-Container Copilot-UserInput-Container--file'>
                <FilePromptComponent
                  file={message.file}
                  userInput={message.userInput}
                />
              </div>
            </div>
          );
        }

        return null;
      })}
    </div>
  );
}

export default ChatFlow;
